import { FC, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Link as MuiLink,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { OpenInNew as OpenInNewIcon } from "@material-ui/icons";
import { Link, useParams } from "react-router-dom";

import { Button } from "components";
import { CONTACT_US_FORM_URL } from "constants/index";
import BaseContainer from "containers/BaseContainer";
import { formatESTDate } from "lib/format";
import { formatMoney } from "lib/helpers";
import * as routes from "routes";
import { usePaymentRequestByToken } from "services/paymentRequests";

import CopyRow from "./components/CopyRow";
import PaymentModal from "./components/PaymentModal";

const useStyles = makeStyles(theme => ({
  finePrint: { color: theme.palette.text.secondary },
  card: { width: "100%", maxWidth: "700px" },
}));

const PaymentPortal: FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const { token } = useParams<{ token: string }>();
  const { data: paymentRequest, isLoading, error } = usePaymentRequestByToken(
    token,
  );
  const isXsDown = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <>
      {paymentRequest && showPaymentForm && (
        <PaymentModal onClose={() => setShowPaymentForm(false)} token={token} />
      )}
      <BaseContainer>
        <Box
          marginTop={theme.spacing(0.5)}
          marginBottom={theme.spacing(1)}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gridGap={theme.spacing(2)}
        >
          {isLoading ? (
            <CircularProgress />
          ) : error ||
            !paymentRequest ||
            paymentRequest.state === "expired" ||
            paymentRequest.state === "inactive" ? (
            <>
              <Typography variant="h5" gutterBottom>
                Payment Link Invalid
              </Typography>
              <MuiLink
                href={CONTACT_US_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                underline="hover"
                variant="body1"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gridGap={theme.spacing(1)}
                  justifyContent="center"
                >
                  Please Contact Us.
                  <OpenInNewIcon
                    style={{
                      fontSize: 18,
                    }}
                  />
                </Box>
              </MuiLink>
            </>
          ) : (
            <>
              <Card elevation={3} className={classes.card}>
                <CardContent>
                  <Box display="flex" flexDirection="column">
                    <Box mb={2}>
                      <Typography variant="h5" gutterBottom>
                        Asset Payment Summary
                      </Typography>
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gridGap={theme.spacing(1)}
                      mb={1}
                    >
                      <Tooltip title="View asset details">
                        <Link
                          to={routes.orderBookAssetDetail(
                            String(paymentRequest.listing_id),
                          )}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            gridGap={theme.spacing(1)}
                            justifyContent={isXsDown ? "center" : "flex-start"}
                          >
                            <strong>{paymentRequest.listing_title}</strong>
                            <OpenInNewIcon
                              style={{
                                fontSize: 18,
                              }}
                            />
                          </Box>
                        </Link>
                      </Tooltip>
                      <Typography>
                        Amount:{" "}
                        <strong>${formatMoney(paymentRequest.amount)}</strong>
                      </Typography>
                    </Box>
                    {paymentRequest.state === "paid" ? (
                      <>
                        <Box mb={4}>
                          <Typography
                            variant="caption"
                            style={{
                              color: theme.palette.success.main,
                            }}
                          >
                            Payment completed.
                          </Typography>
                          <br />
                          <Typography variant="caption">
                            We will contact you to confirm this transaction.
                          </Typography>
                        </Box>
                        <Button
                          fullWidth
                          customColor="green"
                          variant="contained"
                          to="/overview"
                          component={Link}
                        >
                          See Current Listings
                        </Button>
                      </>
                    ) : paymentRequest.expiration &&
                      paymentRequest.state === "active" ? (
                      <Typography variant="caption">
                        This payment link will expire on{" "}
                        <strong>
                          {formatESTDate(paymentRequest.expiration)}
                        </strong>
                      </Typography>
                    ) : null}
                  </Box>
                </CardContent>
              </Card>
              {paymentRequest.state === "active" && (
                <>
                  <Card elevation={3} className={classes.card}>
                    <CardHeader title="Option 1: Instant Payment with ACH Transfer" />
                    <CardContent>
                      <Box display="flex" flexDirection="column">
                        <Box>
                          <Typography paragraph>
                            Securely sign in with your online banking account.
                            We use Plaid to support instant ACH payments. Your
                            credentials are encrypted and never stored on our
                            servers.
                          </Typography>
                          <Box>
                            <Button
                              customColor="green"
                              variant="contained"
                              onClick={() => setShowPaymentForm(true)}
                              disabled={
                                Number(paymentRequest.amount || 0) === 0 ||
                                paymentRequest.state !== "active"
                              }
                              fullWidth
                            >
                              Pay with ACH
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                  <Card elevation={3} className={classes.card}>
                    <CardHeader title="Option 2: Wire Transfer" />
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Box mb={1}>
                            <Typography variant="subtitle2">Bank</Typography>
                            <Typography variant="body1">
                              Wells Fargo Bank
                            </Typography>
                            <Box mt={1}>
                              <Typography variant="caption">Address</Typography>
                            </Box>
                            <Box mt={1}>
                              <Box component="address">
                                <Typography variant="body2">
                                  420 Montgomery Street
                                </Typography>
                                <Typography variant="body2">
                                  San Francisco, CA 94104
                                </Typography>
                                <Typography variant="body2">USA</Typography>
                              </Box>
                            </Box>
                          </Box>
                          <CopyRow label="Wire ABA" value="121000248" />
                          <CopyRow
                            label="SWIFT (International)"
                            value="WFBIUS6S"
                          />
                          <CopyRow label="Account #" value="6597356887" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box mb={1}>
                            <Typography variant="subtitle2">
                              Beneficiary
                            </Typography>
                            <Typography variant="body1">
                              Royalty Exchange Inc.
                            </Typography>
                            <Box mt={1}>
                              <Typography variant="caption">Address</Typography>
                            </Box>
                            <Box mt={1}>
                              <Box component="address">
                                <Typography variant="body2">
                                  1550 Larimer St. #769
                                </Typography>
                                <Typography variant="body2">
                                  Denver, CO 80202
                                </Typography>
                                <Typography variant="body2">USA</Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </>
              )}
            </>
          )}
        </Box>
      </BaseContainer>
    </>
  );
};

export default PaymentPortal;
